import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Lightbulb, 
  Loader2, 
  ChevronDown, 
  ChevronUp, 
  Sparkles,
  MessageCircle,
  Target,
  TrendingUp,
  Star,
  AlertCircle
} from 'lucide-react'
import { KPADiscussionGuidance } from '@/services/aiService'

interface KPADiscussionGuideProps {
  guidance: KPADiscussionGuidance | null
  isLoading: boolean
  onGenerateGuidance: () => void
  isVisible: boolean
}

export function KPADiscussionGuide({ 
  guidance, 
  isLoading, 
  onGenerateGuidance,
  isVisible
}: KPADiscussionGuideProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!isVisible) return null

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'exceptional':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'needs_discussion':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'missing_context':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'exceptional':
        return <Star className="h-3 w-3" />
      case 'needs_discussion':
        return <MessageCircle className="h-3 w-3" />
      case 'missing_context':
        return <AlertCircle className="h-3 w-3" />
      default:
        return null
    }
  }

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <Lightbulb className="h-5 w-5 text-indigo-600" />
            <h4 className="font-semibold text-indigo-900">AI Discussion Guide</h4>
            {guidance && (
              <Badge variant="secondary" className="text-xs">
                Impact Portfolio-based suggestions
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!guidance && (
              <Button
                size="sm"
                variant="default"
                onClick={onGenerateGuidance}
                disabled={isLoading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Get AI Guidance
                  </>
                )}
              </Button>
            )}
            {guidance && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onGenerateGuidance}
                  disabled={isLoading}
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Collapsed Summary */}
        {guidance && !isExpanded && (
          <CardContent className="pt-3 pb-4">
            <div className="flex items-center justify-between">
              <div className="text-sm space-y-1">
                <div>
                  <span className="font-medium text-gray-900">
                    {guidance.celebrationPrompts.length} celebration prompts
                  </span>
                  <span className="text-gray-600 mx-2">•</span>
                  <span className="text-gray-600">
                    {guidance.rootCauseQuestions.length} root cause questions
                  </span>
                  {guidance.developmentSuggestions.length > 0 && (
                    <>
                      <span className="text-gray-600 mx-2">•</span>
                      <span className="text-gray-600">
                        {guidance.developmentSuggestions.length} development ideas
                      </span>
                    </>
                  )}
                </div>
                {guidance.evidenceHighlights.length > 0 && (
                  <div className="text-indigo-700 font-medium">
                    {guidance.evidenceHighlights.length} impact portfolio highlights
                  </div>
                )}
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setIsExpanded(true)}
                className="text-indigo-600 hover:text-indigo-700"
              >
                View Guide
              </Button>
            </div>
          </CardContent>
        )}

        {/* Expanded Content */}
        {guidance && isExpanded && (
          <CardContent className="px-0 pb-0 pt-4 space-y-4 max-h-[500px] overflow-y-auto">
            {/* Celebration Prompts */}
            {guidance.celebrationPrompts.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-green-700 flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  🎉 Celebration Starters
                </h5>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                  {guidance.celebrationPrompts.map((prompt, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5 font-bold">•</span>
                      <p className="text-sm text-green-900">{prompt}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Root Cause Questions */}
            {guidance.rootCauseQuestions.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-amber-700 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  🤔 Root Cause Questions
                </h5>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  {guidance.rootCauseQuestions.map((question, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5 font-bold">•</span>
                      <p className="text-sm text-amber-900">{question}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Development Suggestions */}
            {guidance.developmentSuggestions.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-blue-700 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  💡 Development Ideas
                </h5>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  {guidance.developmentSuggestions.map((suggestion, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-blue-600 mt-0.5 font-bold">•</span>
                      <p className="text-sm text-blue-900">{suggestion}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evidence Highlights */}
            {guidance.evidenceHighlights.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-purple-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  📊 Impact Portfolio Highlights
                </h5>
                <div className="space-y-2">
                  {guidance.evidenceHighlights.map((highlight, i) => (
                    <div 
                      key={i} 
                      className={`border rounded-lg p-3 ${getCategoryColor(highlight.category)}`}
                    >
                      <div className="flex items-start gap-2 mb-1">
                        {getCategoryIcon(highlight.category)}
                        <p className="text-sm font-medium flex-1">{highlight.evidenceTitle}</p>
                        <Badge variant="outline" className="text-xs">
                          {highlight.category.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-xs pl-5">{highlight.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Discussion Tips */}
            {guidance.discussionTips.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium text-sm text-indigo-700 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  💬 Facilitation Tips
                </h5>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
                  {guidance.discussionTips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-0.5 font-bold">•</span>
                      <p className="text-sm text-indigo-900 italic">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              Generated at {new Date(guidance.generatedAt).toLocaleString()}
            </div>
          </CardContent>
        )}

        {!guidance && !isLoading && (
          <div className="mt-3 text-sm text-indigo-700 text-center py-2">
            Get AI-powered suggestions to guide your discussion
          </div>
        )}
      </div>
    </Card>
  )
}
